import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProfilePicture } from '../utils/profilePicture';
import api from '../services/api';
import SearchableDropdown from '../components/SearchableDropdown';
import AddToTeamDialog from '../components/AddToTeamDialog';
import { useTableSort } from '../hooks/useTableSort';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface Player {
  id: string;
  email?: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  soccerExperience?: string;
  teams: Array<{
    teamId: string;
    teamName?: string;
    role: string;
    position?: string;
    isGuest: boolean;
  }>;
  statistics: {
    matchesPlayed: number;
    goals: number;
    assists: number;
    yellowCards: number;
    redCards: number;
  } | null;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const Players: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [players, setPlayers] = useState<Player[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0
  });

  // Prepare data for sorting
  const playersWithSortData = players.map(player => {
    const age = player.dateOfBirth 
      ? Math.floor((new Date().getTime() - new Date(player.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
      : null;
    return {
      ...player,
      sortName: `${player.firstName} ${player.lastName}`,
      sortAge: age,
      sortGoals: player.statistics?.goals || 0,
      sortAssists: player.statistics?.assists || 0,
      sortMatches: player.statistics?.matchesPlayed || 0,
      sortTeams: player.teams.length
    };
  });
  
  const { sortedData, handleSort, getSortIcon } = useTableSort(playersWithSortData);
  const [teams, setTeams] = useState<Array<{ id: string; label: string }>>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [experience, setExperience] = useState<string>('');
  const [minAge, setMinAge] = useState<string>('');
  const [maxAge, setMaxAge] = useState<string>('');
  const [minGoals, setMinGoals] = useState<string>('');
  const [maxGoals, setMaxGoals] = useState<string>('');
  const [minAssists, setMinAssists] = useState<string>('');
  const [maxAssists, setMaxAssists] = useState<string>('');
  const [minMatches, setMinMatches] = useState<string>('');
  const [maxMatches, setMaxMatches] = useState<string>('');
  const [minYellowCards, setMinYellowCards] = useState<string>('');
  const [maxYellowCards, setMaxYellowCards] = useState<string>('');
  const [minRedCards, setMinRedCards] = useState<string>('');
  const [maxRedCards, setMaxRedCards] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [addToTeamPlayer, setAddToTeamPlayer] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  // Debounce search term to avoid losing focus while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchPlayers(1);
    }, 500); // Debounce search by 500ms

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Separate useEffect for other filters (no debouncing needed)
  useEffect(() => {
    setCurrentPage(1);
    fetchPlayers(1);
  }, [
    selectedTeam, experience, minAge, maxAge,
    minGoals, maxGoals, minAssists, maxAssists, minMatches, maxMatches,
    minYellowCards, maxYellowCards, minRedCards, maxRedCards
  ]);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/teams', { params: { page: '1', limit: '100' } });
      const teamsData = response.data.teams || response.data;
      setTeams(teamsData
        .filter((t: any) => t && t.id && t.name)
        .map((t: any) => ({ id: t.id, label: t.name || 'Unknown Team' }))
      );
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchPlayers = async (page: number = 1) => {
    setLoading(true);
    try {
      const params: any = {
        page: page.toString(),
        limit: '100'
      };
      
      if (searchTerm) params.search = searchTerm;
      if (selectedTeam) params.teamId = selectedTeam;
      if (experience) params.experience = experience;
      if (minAge) params.minAge = minAge;
      if (maxAge) params.maxAge = maxAge;
      if (minGoals) params.minGoals = minGoals;
      if (maxGoals) params.maxGoals = maxGoals;
      if (minAssists) params.minAssists = minAssists;
      if (maxAssists) params.maxAssists = maxAssists;
      if (minMatches) params.minMatches = minMatches;
      if (maxMatches) params.maxMatches = maxMatches;
      if (minYellowCards) params.minYellowCards = minYellowCards;
      if (maxYellowCards) params.maxYellowCards = maxYellowCards;
      if (minRedCards) params.minRedCards = minRedCards;
      if (maxRedCards) params.maxRedCards = maxRedCards;

      const response = await api.get('/players', { params });
      setPlayers(response.data.players || response.data);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchPlayers(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTeam('');
    setExperience('');
    setMinAge('');
    setMaxAge('');
    setMinGoals('');
    setMaxGoals('');
    setMinAssists('');
    setMaxAssists('');
    setMinMatches('');
    setMaxMatches('');
    setMinYellowCards('');
    setMaxYellowCards('');
    setMinRedCards('');
    setMaxRedCards('');
    setCurrentPage(1);
  };

  const handleAddToTeam = (playerId: string, playerName: string) => {
    setAddToTeamPlayer({ id: playerId, name: playerName });
  };

  if (loading && players.length === 0) {
    return <LoadingSpinner fullPage />;
  }

  const isManager = user?.profileType === 'manager' || isAdminOrSuperAdmin(user?.profileType);

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Players</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          {isManager && (
            <button className="btn btn-primary" onClick={() => navigate('/register')}>
              Register New Player
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="form-group">
          <label>Search Players</label>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        {showFilters && (
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #E0E0E0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
              <div className="form-group">
                <label>Filter by Team</label>
                <SearchableDropdown
                  options={teams}
                  value={selectedTeam}
                  onChange={setSelectedTeam}
                  placeholder="Search and select team..."
                />
              </div>

              <div className="form-group">
                <label>Experience Level</label>
                <select
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">All Levels</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>

              <div className="form-group">
                <label>Min Age</label>
                <input
                  type="number"
                  placeholder="Min age"
                  value={minAge}
                  onChange={(e) => setMinAge(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Max Age</label>
                <input
                  type="number"
                  placeholder="Max age"
                  value={maxAge}
                  onChange={(e) => setMaxAge(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Min Goals</label>
                <input
                  type="number"
                  placeholder="Min goals"
                  value={minGoals}
                  onChange={(e) => setMinGoals(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Max Goals</label>
                <input
                  type="number"
                  placeholder="Max goals"
                  value={maxGoals}
                  onChange={(e) => setMaxGoals(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Min Assists</label>
                <input
                  type="number"
                  placeholder="Min assists"
                  value={minAssists}
                  onChange={(e) => setMinAssists(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Max Assists</label>
                <input
                  type="number"
                  placeholder="Max assists"
                  value={maxAssists}
                  onChange={(e) => setMaxAssists(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Min Matches</label>
                <input
                  type="number"
                  placeholder="Min matches"
                  value={minMatches}
                  onChange={(e) => setMinMatches(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Max Matches</label>
                <input
                  type="number"
                  placeholder="Max matches"
                  value={maxMatches}
                  onChange={(e) => setMaxMatches(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Min Yellow Cards</label>
                <input
                  type="number"
                  placeholder="Min yellow cards"
                  value={minYellowCards}
                  onChange={(e) => setMinYellowCards(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Max Yellow Cards</label>
                <input
                  type="number"
                  placeholder="Max yellow cards"
                  value={maxYellowCards}
                  onChange={(e) => setMaxYellowCards(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Min Red Cards</label>
                <input
                  type="number"
                  placeholder="Min red cards"
                  value={minRedCards}
                  onChange={(e) => setMinRedCards(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Max Red Cards</label>
                <input
                  type="number"
                  placeholder="Max red cards"
                  value={maxRedCards}
                  onChange={(e) => setMaxRedCards(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ marginTop: '15px' }}>
              <button className="btn btn-secondary" onClick={clearFilters}>
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2>Player Directory ({pagination.total} players)</h2>
          {pagination.totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {players.length === 0 ? (
          <p>No players found matching your filters.</p>
        ) : (
          <>
            <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('sortName' as any)}>
                    Name {getSortIcon('sortName' as any)}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('soccerExperience' as any)}>
                    Experience {getSortIcon('soccerExperience' as any)}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('sortAge' as any)}>
                    Age {getSortIcon('sortAge' as any)}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('sortTeams' as any)}>
                    Teams {getSortIcon('sortTeams' as any)}
                  </th>
                  <th>Statistics</th>
                  {isManager && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sortedData.map((player) => {
                  const age = player.dateOfBirth 
                    ? Math.floor((new Date().getTime() - new Date(player.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
                    : null;

                  return (
                    <tr key={player.id} className="clickable" onClick={() => navigate(`/users/${player.id}`)}>
                      <td>
                        <img 
                          src={getProfilePicture(player)} 
                          alt={`${player.firstName} ${player.lastName}`}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                      </td>
                      <td>
                        <Link to={`/users/${player.id}`} onClick={(e) => e.stopPropagation()}>
                          <strong>{player.firstName} {player.lastName}</strong>
                        </Link>
                      </td>
                      <td>{player.soccerExperience || 'N/A'}</td>
                      <td>{age !== null ? `${age} years` : 'N/A'}</td>
                      <td>
                        {player.teams.length === 0 ? (
                          <span style={{ color: '#9E9E9E' }}>No teams</span>
                        ) : (
                          <div>
                            {player.teams.map((team, idx) => (
                              <span key={idx} className="badge badge-info" style={{ marginRight: '5px' }}>
                                {team.teamName || 'Unknown'} {team.isGuest && '(Guest)'}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        {player.statistics ? (
                          <div style={{ fontSize: '14px' }}>
                            <div>⚽ {player.statistics.goals} goals</div>
                            <div>🎯 {player.statistics.assists} assists</div>
                            <div>📊 {player.statistics.matchesPlayed} matches</div>
                            {(player.statistics.yellowCards > 0 || player.statistics.redCards > 0) && (
                              <div>
                                {player.statistics.yellowCards > 0 && (
                                  <span className="badge badge-warning" style={{ marginRight: '5px' }}>
                                    🟨 {player.statistics.yellowCards}
                                  </span>
                                )}
                                {player.statistics.redCards > 0 && (
                                  <span className="badge badge-danger">
                                    🟥 {player.statistics.redCards}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#9E9E9E' }}>No stats</span>
                        )}
                      </td>
                      {isManager && (
                        <td>
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: '12px', padding: '5px 10px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToTeam(player.id, `${player.firstName} ${player.lastName}`);
                            }}
                          >
                            Add to Team
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.totalPages} (Showing {players.length} of {pagination.total} players)
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= pagination.totalPages}
                >
                  Next
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={currentPage >= pagination.totalPages}
                >
                  Last
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {addToTeamPlayer && (
        <AddToTeamDialog
          playerId={addToTeamPlayer.id}
          playerName={addToTeamPlayer.name}
          isOpen={true}
          onClose={() => setAddToTeamPlayer(null)}
          onSuccess={() => fetchPlayers(currentPage)}
        />
      )}
    </div>
  );
};

export default Players;
